import type { MinusAuctionPlayer, ScoreEntry } from "../types";

export interface CubeRun {
  readonly cubes: readonly number[];
  readonly scoredCube: number;
  readonly eliminatedCubes: readonly number[];
}

export function groupConsecutiveCubes(
  cubes: readonly number[],
): readonly CubeRun[] {
  const sorted = [...new Set(cubes)].sort((left, right) => left - right);
  const runs: CubeRun[] = [];
  let currentRun: number[] = [];

  for (const cube of sorted) {
    const previous = currentRun.at(-1);
    if (previous === undefined || cube - previous === 1) {
      currentRun = [...currentRun, cube];
      continue;
    }

    runs.push(toCubeRun(currentRun));
    currentRun = [cube];
  }

  if (currentRun.length > 0) {
    runs.push(toCubeRun(currentRun));
  }

  return runs;
}

export function calculateCubeScore(cubes: readonly number[]): {
  readonly scoredCubeSum: number;
  readonly scoredCubes: readonly number[];
  readonly eliminatedCubes: readonly number[];
  readonly runs: readonly CubeRun[];
} {
  const runs = groupConsecutiveCubes(cubes);
  const scoredCubes = runs.map((run) => run.scoredCube);
  const eliminatedCubes = runs.flatMap((run) => run.eliminatedCubes);
  const scoredCubeSum = scoredCubes.reduce((sum, cube) => sum + cube, 0);

  return { scoredCubeSum, scoredCubes, eliminatedCubes, runs };
}

export function calculateScoreEntry(player: MinusAuctionPlayer): ScoreEntry {
  const cubeScore = calculateCubeScore(player.cubes);
  const rawScore = cubeScore.scoredCubeSum - player.chips;
  return {
    playerId: player.playerId,
    scoredCubeSum: cubeScore.scoredCubeSum,
    remainingChips: player.chips,
    chipSurplus: Math.max(0, player.chips - cubeScore.scoredCubeSum),
    remainingGarnets: player.garnets,
    finalScore: Math.max(0, rawScore),
    scoredCubes: cubeScore.scoredCubes,
    eliminatedCubes: cubeScore.eliminatedCubes,
  };
}

export function calculateScoreEntries(
  players: readonly MinusAuctionPlayer[],
): readonly ScoreEntry[] {
  return players
    .map(calculateScoreEntry)
    .sort(
      (left, right) =>
        left.finalScore - right.finalScore ||
        right.chipSurplus - left.chipSurplus,
    );
}

function toCubeRun(cubes: readonly number[]): CubeRun {
  const [scoredCube, ...eliminatedCubes] = cubes;
  if (scoredCube === undefined) {
    throw new Error("Cannot create a cube run without cubes");
  }
  return { cubes, scoredCube, eliminatedCubes };
}
